import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {history} from 'common/history';
import {color, font, radius, type} from "common/tokens";
import CtaButton from "components/redesign/CtaButton";
import {X} from "lucide-react";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";

// Asked for on the way out of a session: one tap on a face, and a note only if
// the reader has something to say. The rating is the required half, so the
// faces carry the card and the text box stays visibly optional.
const RATINGS = [
  {value: 1, emoji: '😞'},
  {value: 2, emoji: '🙁'},
  {value: 3, emoji: '😐'},
  {value: 4, emoji: '🙂'},
  {value: 5, emoji: '😄'},
];

const useStyles = makeStyles(theme => ({
  card: {
    position: 'relative',
    background: color.ACCENT_TINT,
    borderRadius: radius.CARD,
    boxShadow: '0 10px 14px rgba(0, 0, 0, 0.08)',
    boxSizing: 'border-box',
    width: 624,
    maxWidth: '100%',
    padding: 28,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
  },
  title: {
    ...type.body,
    fontSize: 20,
    fontWeight: font.BOLD,
    color: color.TEXT,
    textAlign: 'center',
    margin: 0,
  },
  prompt: {
    ...type.body,
    fontSize: 14,
    fontWeight: font.BOLD,
    color: color.TEXT,
    textAlign: 'center',
    margin: 0,
  },
  emojiRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 30,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      gap: 12,
    },
  },
  emoji: {
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    lineHeight: 1,
    fontSize: 56,
    cursor: 'pointer',
    // Faded until picked, so the chosen face is the only one at full strength.
    opacity: 0.5,
    transition: 'opacity 120ms ease-out, transform 120ms ease-out',
    '&:hover': {
      opacity: 0.8,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 38,
    },
  },
  emojiSelected: {
    opacity: 1,
    transform: 'scale(1.08)',
  },
  optionalLabel: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    margin: 0,
  },
  optionalStrong: {
    ...type.body,
    fontSize: 14,
    fontWeight: font.SEMIBOLD,
    color: color.TEXT,
    margin: 0,
  },
  optionalHint: {
    ...type.body,
    fontSize: 14,
    fontWeight: font.REGULAR,
    color: color.ACCENT_TEXT,
    margin: 0,
  },
  textarea: {
    ...type.body,
    fontSize: 14,
    color: color.TEXT,
    background: color.SURFACE,
    border: `1.5px solid ${color.BORDER}`,
    borderRadius: radius.PANEL,
    boxSizing: 'border-box',
    width: '100%',
    height: 90,
    padding: '12px 14px',
    resize: 'none',
    outline: 'none',
    '&::placeholder': {
      color: color.BORDER,
    },
    '&:focus': {
      borderColor: color.ACCENT,
    },
  },
  submit: {
    width: 270,
    maxWidth: '100%',
    height: 52,
    borderRadius: radius.PANEL,
    textTransform: 'uppercase',
  },
  close: {
    position: 'absolute',
    top: 16,
    right: 16,
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 4,
    cursor: 'pointer',
    color: color.TEXT_MUTED,
    display: 'flex',
  },
}));

const Feedback = ({isLearningHub, sessionId, submitSessionFeedback}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const closeFeedBack = () => history.push(isLearningHub ? '/learninghub' : '/mentorship');

  const submitFeedBack = async () => {
    setSubmitting(true);
    try {
      await submitSessionFeedback(sessionId, {rating, review}, closeFeedBack);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.card}>
      <button type={'button'} className={classes.close} onClick={closeFeedBack} aria-label={t('closeCaps')}>
        <X size={20} strokeWidth={2}/>
      </button>

      <p className={classes.title}>{t('weAreListening')}</p>
      <p className={classes.prompt}>{t('howWasThisSession')}</p>

      <div className={classes.emojiRow} role={'radiogroup'} aria-label={t('howWasThisSession')}>
        {RATINGS.map(option => (
          <button
            key={option.value}
            type={'button'}
            role={'radio'}
            aria-checked={rating === option.value}
            aria-label={`${option.value}`}
            className={classNames(classes.emoji, rating === option.value && classes.emojiSelected)}
            onClick={() => setRating(option.value)}
          >
            {option.emoji}
          </button>
        ))}
      </div>

      <p className={classes.optionalLabel}>
        <span className={classes.optionalStrong}>{t('anyThoughts')}</span>
        <span className={classes.optionalHint}>{t('optionalLabel')}</span>
      </p>

      <textarea
        className={classes.textarea}
        placeholder={t('feedbackPlaceholder')}
        value={review}
        onChange={event => setReview(event.target.value)}
      />

      <CtaButton
        className={classes.submit}
        variant={'primary'}
        label={t('submitFeedback')}
        loading={submitting}
        disabled={!rating}
        onClick={submitFeedBack}
      />
    </div>
  );
};

export default Feedback;
